class HorizontalCarousel extends HTMLElement {
  constructor() {
    super();

    // Element references
    this.carousel = null;
    this.leftButton = null;
    this.rightButton = null;
    this.progressBar = null;
    
    // Configuration from attributes
    this.scrollAmount = 0;
    this.carouselSelector = this.getAttribute('carousel-selector') || '.overflow-x-scroll';
    this.leftButtonSelector = 'button[data-carousel-dir="left"]';
    this.rightButtonSelector = 'button[data-carousel-dir="right"]';
    this.enableProgressBar = this.getAttribute('enable-progress-bar') === 'true';
    
    // Bound methods for event listeners (for cleanup)
    this.handleClick = this.handleClick.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initialize() {
    // Find required elements
    this.carousel = this.querySelector(this.carouselSelector);
    if (!this.carousel) {
      console.warn(`ProgressCarousel: Carousel element not found with selector "${this.carouselSelector}"`);
      return;
    }

    this.leftButton = this.querySelector(this.leftButtonSelector);
    this.rightButton = this.querySelector(this.rightButtonSelector);
    
    // Find progress bar if enabled
    if (this.enableProgressBar) {
      this.progressBar = this.querySelector('[data-progress-bar]');
    }

    // Attach event listeners
    this.addEventListener('click', this.handleClick);
    
    // Add scroll listener for progress bar
    if (this.enableProgressBar) {
      this.carousel.addEventListener('scroll', this.updateProgress);
      // Set initial progress
      this.updateProgress();
    }
  }

  calculateScrollAmount() {
    const firstItem = this.carousel.querySelector('[data-carousel-item]');
    if (!firstItem) {
      this.scrollAmount = 150;
      return;
    }

    const firstItemStyle = window.getComputedStyle(firstItem);
    const firstItemWidth = firstItem.offsetWidth;
    const firstItemMarginRight = parseFloat(firstItemStyle.marginRight) || 0;
    this.scrollAmount = firstItemWidth + firstItemMarginRight;
  }

  cleanup() {
    if (this.carousel && this.enableProgressBar) {
      this.carousel.removeEventListener('scroll', this.updateProgress);
    }
    
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(e) {
    if (!this.carousel) return;

    if (!this.scrollAmount) this.calculateScrollAmount();

    if (e.target.getAttribute('data-carousel-dir') === 'left') {
      this.carousel.scrollLeft = this.carousel.scrollLeft - this.scrollAmount;
      if (this.enableProgressBar) {
        this.updateProgress();
      }
    } else if (e.target.getAttribute('data-carousel-dir') === 'right') {
      this.carousel.scrollLeft = this.carousel.scrollLeft + this.scrollAmount;
      if (this.enableProgressBar) {
        this.updateProgress();
      }
    }
  }

  updateProgress() {
    if (!this.carousel) return;
    
    const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth;
    let scrollPercentage = maxScroll > 0 ? (this.carousel.scrollLeft / maxScroll) * 100 : 0;

    // Update button visibility
    if (this.leftButton) {
      if (scrollPercentage == 0) {
        this.leftButton.classList.add('hidden');
      } else {
        this.leftButton.classList.remove('hidden');
      }
    }
    if (this.rightButton) {
      if (scrollPercentage == 100) {
        this.rightButton.classList.add('hidden');
      } else {
        this.rightButton.classList.remove('hidden');
      }
    }

    // Update progress bar width (minimum 25%)
    if (this.progressBar) {
      if (scrollPercentage < 25) scrollPercentage = 25;
      this.progressBar.style.width = `${scrollPercentage}%`;
    }
  }

  // Public method to scroll to a specific position
  scrollTo(position) {
    if (this.carousel) {
      this.carousel.scrollLeft = position;
    }
  }
}

// Register the custom element
if (!customElements.get('horizontal-carousel')) {
  customElements.define('horizontal-carousel', HorizontalCarousel);
}
